/**
 * Encounter Documents tab.
 *
 * Drop-zone + list of arbitrary documents attached to this encounter.
 * Different from PreviousInvestigationsTab — that tab is for prior-care
 * lab/imaging reports the patient brought in (with a date/notes shape).
 * This tab is for anything else generated *during* this visit: lab
 * requisitions, ECG strips, ID/insurance scans, signed consent, photos.
 */
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    TextField,
    Stack,
    Paper,
    LinearProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import Chip from '@mui/material/Chip';
import {
    uploadEncounterDocument,
    deleteEncounterDocument,
    setEncounterDocumentInclusion,
    encounterDocumentDownloadUrl,
} from '../../../../api/encounter';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';
const MAX_BYTES = 15 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,application/pdf,.doc,.docx';

// Types whose pages can actually be merged into the summary PDF. Mirrors
// APPENDABLE_MIME in server/helper/reportAttachments.js — DOC/DOCX upload
// fine but have no page representation to append, so the checkbox is
// offered but disabled for them rather than silently ignored.
const APPENDABLE_MIME = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const canAppend = (mime) => APPENDABLE_MIME.has(mime);

function humanSize(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentsTab({ encounter, onReload, disabled }) {
    const fileInput = useRef(null);
    const navigate = useNavigate();
    const { role: roleParam } = useParams();
    const [pending, setPending] = useState(null);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);

    const items = encounter?.documents || [];

    const onPickFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > MAX_BYTES) {
            setError(`File too large (${humanSize(file.size)}). Maximum is ${humanSize(MAX_BYTES)}.`);
            return;
        }
        setError(null);
        const isImage = file.type.startsWith('image/');
        const preview = isImage ? URL.createObjectURL(file) : null;
        setPending({
            file,
            label: file.name.replace(/\.[^.]+$/, ''),
            preview,
            includeInReport: false,
        });
    };

    const cancelPending = () => {
        if (pending?.preview) URL.revokeObjectURL(pending.preview);
        setPending(null);
        setProgress(null);
        setError(null);
    };

    const confirmUpload = async () => {
        if (!pending) return;
        setProgress(5);
        const tick = setInterval(() => {
            setProgress((p) => (p === null ? p : Math.min(90, p + 5)));
        }, 120);
        try {
            await uploadEncounterDocument(
                encounter._id,
                pending.file,
                pending.label,
                pending.includeInReport,
            );
            clearInterval(tick);
            setProgress(100);
            await onReload?.();
            setTimeout(cancelPending, 250);
        } catch (err) {
            clearInterval(tick);
            setProgress(null);
            setError(err?.body?.message || err?.message || 'Upload failed.');
        }
    };

    const toggleInclusion = async (docId, next) => {
        setTogglingId(docId);
        setError(null);
        try {
            await setEncounterDocumentInclusion(encounter._id, docId, next);
            await onReload?.();
        } catch (err) {
            setError(err?.body?.message || err?.message || 'Could not update the report setting.');
        } finally {
            setTogglingId(null);
        }
    };

    const remove = async (docId) => {
        setDeletingId(docId);
        try {
            await deleteEncounterDocument(encounter._id, docId);
            await onReload?.();
        } catch (err) {
            setError(err?.body?.message || err?.message || 'Delete failed.');
        } finally {
            setDeletingId(null);
        }
    };

    const openPatientDocs = () => {
        navigate(`/${roleParam || 'admin'}/patients/${encounter.patientId}/documents`);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <SectionHeading
                    title="Documents"
                    hint="Lab requisitions, ECG strips, ID / insurance scans, signed consent, photos — anything attached to this visit."
                />
                <Button
                    size="small"
                    startIcon={<FolderOpenRoundedIcon />}
                    onClick={openPatientDocs}
                    sx={{ textTransform: 'none', fontWeight: 600, color: NAVY, whiteSpace: 'nowrap' }}
                >
                    All patient documents
                </Button>
            </Box>

            {!disabled && (
                <Paper
                    variant="outlined"
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer.files?.[0];
                        if (f) onPickFile({ target: { files: [f], value: '' } });
                    }}
                    sx={{
                        p: 3,
                        mb: 2,
                        borderRadius: 2,
                        borderStyle: 'dashed',
                        borderColor: 'rgba(5,47,93,0.25)',
                        bgcolor: 'rgba(5,47,93,0.02)',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 150ms ease, border-color 150ms ease',
                        '&:hover': { bgcolor: 'rgba(5,47,93,0.04)', borderColor: NAVY },
                    }}
                    onClick={() => fileInput.current?.click()}
                >
                    <input
                        ref={fileInput}
                        type="file"
                        hidden
                        accept={ACCEPT}
                        onChange={onPickFile}
                    />
                    <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: NAVY, opacity: 0.6 }} />
                    <Typography sx={{ mt: 1, fontWeight: 600, color: NAVY }}>
                        Drag a file here, or click to browse
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        JPG, PNG, PDF, DOC, or DOCX up to {humanSize(MAX_BYTES)}.
                    </Typography>
                </Paper>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {items.length === 0 ? (
                <Box
                    sx={{
                        py: 3,
                        px: 2,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(5,47,93,0.025)',
                        border: '1px dashed rgba(5,47,93,0.15)',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No documents attached yet.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1}>
                    {items.map((it) => {
                        const isSummary = it.kind === 'summary';
                        // The report as it was signed before an amendment
                        // replaced it. Kept permanently and read-only: it is
                        // the evidence the amendment trail exists to preserve.
                        const isSuperseded = it.kind === 'superseded_summary';
                        // System-generated either way — not the clinician's to
                        // delete or to append to the report.
                        const isSystem = isSummary || isSuperseded;
                        const appendable = canAppend(it.mime);
                        return (
                            <Paper
                                key={it._id}
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    borderColor: isSummary
                                        ? 'rgba(46,125,50,0.30)'
                                        : isSuperseded ? 'rgba(0,0,0,0.14)' : 'rgba(5,47,93,0.10)',
                                    bgcolor: isSummary
                                        ? 'rgba(46,125,50,0.04)'
                                        : isSuperseded ? 'rgba(0,0,0,0.02)' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                {isSystem
                                    ? <DescriptionOutlinedIcon sx={{ color: isSummary ? 'success.dark' : 'text.disabled' }} />
                                    : <InsertDriveFileOutlinedIcon sx={{ color: NAVY }} />}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                color: isSummary ? 'success.dark' : isSuperseded ? 'text.secondary' : NAVY,
                                                lineHeight: 1.25,
                                            }}
                                            noWrap
                                        >
                                            {it.label || it.originalName || 'Document'}
                                        </Typography>
                                        {isSummary && (
                                            <Chip
                                                size="small"
                                                label="Auto-generated"
                                                sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: 'rgba(46,125,50,0.10)', color: 'success.dark', border: '1px solid rgba(46,125,50,0.25)' }}
                                            />
                                        )}
                                        {isSuperseded && (
                                            <Chip
                                                size="small"
                                                label="Superseded"
                                                sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: 'rgba(0,0,0,0.05)', color: 'text.secondary', border: '1px solid rgba(0,0,0,0.15)' }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                                        {it.originalName || ''}
                                        {it.sizeBytes ? ` · ${humanSize(it.sizeBytes)}` : ''}
                                        {it.uploadedAt ? ` · ${new Date(it.uploadedAt).toLocaleString()}` : ''}
                                    </Typography>
                                </Box>
                                {!isSystem && (
                                    <Tooltip
                                        title={
                                            appendable
                                                ? 'Append the full document to the visit summary report'
                                                : 'Only PDF, JPG, and PNG files can be appended to the report'
                                        }
                                        arrow
                                    >
                                        <span>
                                            <FormControlLabel
                                                sx={{ mr: 0.5, ml: 0 }}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={Boolean(it.includeInReport)}
                                                        onChange={(e) => toggleInclusion(it._id, e.target.checked)}
                                                        disabled={disabled || !appendable || togglingId === it._id}
                                                        sx={{ color: NAVY, '&.Mui-checked': { color: NAVY } }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                        In report
                                                    </Typography>
                                                }
                                            />
                                        </span>
                                    </Tooltip>
                                )}
                                <Tooltip title="Open" arrow>
                                    <IconButton
                                        component="a"
                                        href={encounterDocumentDownloadUrl(encounter._id, it._id)}
                                        target="_blank"
                                        rel="noreferrer"
                                        size="small"
                                        sx={{ color: isSummary ? 'success.dark' : isSuperseded ? 'text.secondary' : NAVY }}
                                    >
                                        <OpenInNewRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {!disabled && !isSystem && (
                                    <Tooltip title="Delete" arrow>
                                        <span>
                                            <IconButton
                                                onClick={() => remove(it._id)}
                                                disabled={deletingId === it._id}
                                                size="small"
                                                sx={{ color: 'error.main' }}
                                            >
                                                <DeleteOutlineRoundedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Paper>
                        );
                    })}
                </Stack>
            )}

            <Dialog open={Boolean(pending)} onClose={progress !== null ? undefined : cancelPending} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: NAVY, fontWeight: 600 }}>Attach document</DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    {pending && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                {pending.preview ? (
                                    <Box
                                        component="img"
                                        src={pending.preview}
                                        alt="Preview"
                                        sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1.5, border: '1px solid rgba(5,47,93,0.1)' }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 96, height: 96, borderRadius: 1.5,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: 'rgba(5,47,93,0.04)', border: '1px solid rgba(5,47,93,0.1)',
                                        }}
                                    >
                                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: NAVY, opacity: 0.7 }} />
                                    </Box>
                                )}
                                <Box>
                                    <Typography sx={{ fontWeight: 600 }}>{pending.file.name}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {pending.file.type || 'unknown type'} · {humanSize(pending.file.size)}
                                    </Typography>
                                </Box>
                            </Box>
                            <TextField
                                label="Label"
                                fullWidth
                                size="small"
                                value={pending.label}
                                onChange={(e) => setPending({ ...pending, label: e.target.value })}
                                disabled={progress !== null}
                                helperText="Short description shown in the list."
                            />
                            <Box sx={{ mt: 1.5 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={Boolean(pending.includeInReport)}
                                            onChange={(e) => setPending({ ...pending, includeInReport: e.target.checked })}
                                            disabled={progress !== null || !canAppend(pending.file.type)}
                                            sx={{ color: NAVY, '&.Mui-checked': { color: NAVY } }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            Append to the visit summary report
                                        </Typography>
                                    }
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 4, mt: -0.5 }}>
                                    {canAppend(pending.file.type)
                                        ? 'The full document is added after the signature on the generated report.'
                                        : 'Only PDF, JPG, and PNG files can be appended. This file stays on the encounter.'}
                                </Typography>
                            </Box>
                            {progress !== null && (
                                <Box sx={{ mt: 2 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progress}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(5,47,93,0.08)',
                                            '& .MuiLinearProgress-bar': { bgcolor: NAVY },
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                                        Uploading… {progress}%
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={cancelPending} disabled={progress !== null && progress < 100} sx={{ color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmUpload}
                        disabled={progress !== null || !pending?.label?.trim()}
                        variant="contained"
                        startIcon={<CloudUploadOutlinedIcon />}
                        sx={{
                            bgcolor: NAVY,
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                        }}
                    >
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
